/**
 * Global Resume Limit Context
 * Manages the centralized resume limit reached modal across the entire app
 */

import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

import { ResumeLimitModal } from "../components/modals/resume-limit-modal";

type ResumeLimitData = {
  errorMessage?: string;
  currentCount?: number;
  maxAllowed?: number;
};

type ResumeLimitContextType = {
  showResumeLimitModal: (data?: ResumeLimitData) => void;
  hideResumeLimitModal: () => void;
  isModalOpen: boolean;
  handleLimitReached: (error?: unknown) => boolean;
};

const ResumeLimitContext = createContext<ResumeLimitContextType | null>(null);

export const useResumeLimit = () => {
  const context = useContext(ResumeLimitContext);
  if (!context) {
    throw new Error("useResumeLimit must be used within a ResumeLimitProvider");
  }
  return context;
};

type ResumeLimitProviderProps = {
  children: ReactNode;
};

export const ResumeLimitProvider = ({ children }: ResumeLimitProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<ResumeLimitData | null>(null);

  const showResumeLimitModal = useCallback((data?: ResumeLimitData) => {
    setModalData(data ?? null);
    setIsModalOpen(true);
  }, []);

  const hideResumeLimitModal = useCallback(() => {
    setIsModalOpen(false);
    setModalData(null);
  }, []);

  const handleLimitReached = useCallback(
    (error?: unknown) => {
      if (!error) {
        showResumeLimitModal();
        return true;
      }

      // Extract message from axios error response if available, otherwise fall back to error.message
      const axiosMessage = (error as any)?.response?.data?.message;
      const message =
        typeof error === "string" 
          ? error 
          : axiosMessage ?? (error as Error).message ?? String(error);
      const lowerMessage = message.toLowerCase();
      
      // Check for various resume limit error patterns
      const isLimitError =
        lowerMessage.includes("limit") ||
        lowerMessage.includes("upgrade") ||
        lowerMessage.includes("free users can create") ||
        lowerMessage.includes("create up to") ||
        (typeof error === "object" &&
          error !== null &&
          (error as Record<string, unknown>).error === "RESUME_LIMIT_REACHED");

      if (isLimitError) {
        showResumeLimitModal({
          errorMessage: message,
        });
        return true;
      }

      return false;
    },
    [showResumeLimitModal],
  );

  const contextValue = {
    showResumeLimitModal,
    hideResumeLimitModal,
    handleLimitReached,
    isModalOpen,
  };

  return (
    <ResumeLimitContext.Provider value={contextValue}>
      {children}

      {/* Global Resume Limit Modal */}
      <ResumeLimitModal
        isOpen={isModalOpen}
        onClose={hideResumeLimitModal}
        errorMessage={modalData?.errorMessage}
        currentCount={modalData?.currentCount}
        maxAllowed={modalData?.maxAllowed}
      />
    </ResumeLimitContext.Provider>
  );
};
